<script setup lang="ts">
import VueMultiselect from 'vue-multiselect'

import queryString from 'query-string'

import { getStringOrFirstElement } from '@/utils/array'
import { titleCase } from '@/utils/string'
import type { AnyContent, Tag } from '@/models/content'
import type { SearchQueryParams } from '@/models/request'
import { allMediums } from '@/constants/content'
import { logError } from '@/utils/log-error'

import ArchiveGeneralCard from '@/components/cards/ArchiveGeneralCard.vue'
import ArchiveMagazineCard from '@/components/cards/ArchiveMagazineCard.vue'
import ArchivePresentationCard from '@/components/cards/ArchivePresentationCard.vue'

import IconRefresh from '@/assets/icons/refresh.svg'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'

const { $restClient, $seoManager } = useNuxtApp()
const route = useRoute()
const router = useRouter()

const tag = getStringOrFirstElement(route.params.tag)
const readableTag = titleCase(tag.replace(/-/g, ' '))
const isDataVisualization = tag === 'data-visualisation'
const pageContents = ref<AnyContent[]>()
const loading = ref(true)
const seo = $seoManager.generateSeoInfo({ title: `Tag: ${readableTag}` })
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const MAX_ITEMS_PER_PAGE = 12
const mediumsSelected = ref([])

let firstLoad = true

const pageCounter = ref(1)
const totalPages = ref(0)
const initialPage = ref(1)
const paginationReset = ref(0)
const totalResults = ref(0)
const arrayFormat = 'bracket'

const initialParams: SearchQueryParams = queryString.parse(
  route.fullPath.split('?')[1],
  {
    arrayFormat,
  },
)
initialParams.items_per_page = MAX_ITEMS_PER_PAGE

function setInitialFilters() {
  mediumsSelected.value =
    initialParams.post_types?.reduce((result, current) => {
      const found = allMediums.find((obj) => obj.value === current)

      if (found) result.push(found)

      return result
    }, []) || []

  initialPage.value =
    Math.ceil(initialParams?.page_offset / initialParams?.items_per_page + 1) ||
    1
  pageCounter.value = initialPage.value
}

setInitialFilters()

async function fetchContents() {
  if (firstLoad) pageCounter.value = initialPage.value

  firstLoad = false

  const queryStringParams = {
    post_types: mediumsSelected.value.map((obj) => obj.value),
    tags: [tag],
    page_offset: MAX_ITEMS_PER_PAGE * (pageCounter.value - 1),
    items_per_page: MAX_ITEMS_PER_PAGE,
  }

  try {
    const url = `${route.path}?${queryString.stringify(queryStringParams, { arrayFormat })}`
    await router.push(url)
    loading.value = true

    const { data, total } = await $restClient.getSearchResult(queryStringParams)

    pageContents.value = data

    totalResults.value = total
    totalPages.value = Math.ceil(total / MAX_ITEMS_PER_PAGE)

    loading.value = false
  } catch (e) {
    logError(e)
  }
}

watch(pageCounter, () => {
  if (!firstLoad) fetchContents()
})

watch(totalPages, () => paginationReset.value++)

watch(
  () => ({
    medium: mediumsSelected.value,
  }),
  () => {
    if (!firstLoad) {
      pageCounter.value = 1
      paginationReset.value++
      fetchContents()
    }
  },
)

const cards = {
  article: ArchiveGeneralCard,
  video: ArchiveGeneralCard,
  magazine: ArchiveMagazineCard,
  presentation: ArchivePresentationCard,
}

function filterTags(tags: Tag[]) {
  return tags.filter((_tag) => _tag.slug !== tag)
}

onMounted(fetchContents)

function onPageNumberChange(counter) {
  pageCounter.value = counter
}
</script>

<template>
  <div>
    <header class="bg-s-100 pb-8 pt-10">
      <div class="mx-auto max-w-screen-lg px-5">
        <h1 class="mb-6 text-4xl font-bold">Tag: {{ readableTag }}</h1>

        <div
          v-if="!isDataVisualization"
          class="multiselect-container md:[&>div]:w-[15.625rem]"
        >
          <div>
            <div class="flex items-center justify-between">
              <label for="mediums" class="py-2 text-xs font-semibold">
                Formats
              </label>
              <IconRefresh
                class="ml-2 mr-1 h-4 w-4 cursor-pointer stroke-black transition-transform active:rotate-45 active:stroke-s-900"
                data-testid="multiselect-formats-reset"
                @click="mediumsSelected = []"
              />
            </div>
            <VueMultiselect
              id="mediums"
              v-model="mediumsSelected"
              :options="allMediums"
              track-by="label"
              label="label"
              :close-on-select="false"
              :multiple="true"
              :taggable="true"
              placeholder="All formats"
              class="mb-2 rounded border-2 border-s-300"
              data-testid="multiselect-formats"
            />
          </div>
        </div>
      </div>
    </header>

    <LoadingSpinner
      v-if="loading"
      class="h-[calc(100vh-301px)] md:h-[calc(100vh-301px)] lg:h-[calc(100vh-470px)]"
      data-testid="search-spinner"
    />

    <NoResultsMessage v-else-if="!pageContents.length" :query="readableTag" />

    <section v-else class="mt-10 grow px-5 lg:mx-auto lg:max-w-screen-lg">
      <div
        v-for="content in pageContents"
        :key="content.id"
        class="border-b-2 border-s-300"
      >
        <component
          :is="cards[content.contentType]"
          :pills="filterTags(content.tags)"
          :pills-limit="2"
          v-bind="content"
          :data-testid="`content-type-${content.contentType}`"
        />
      </div>
    </section>

    <div
      class="px-5 lg:mx-auto lg:max-w-screen-lg"
      :class="(totalResults && !loading) || 'opacity-0'"
    >
      <PageNavigationControls
        :key="paginationReset"
        :total-pages="totalPages"
        :initial-page="pageCounter"
        :on-page-number-change="onPageNumberChange"
        scroll-on-change
      />
    </div>
  </div>
</template>
